/**
 * OverviewTab — marketing KPIs (30d), weekly engagement chart (12w), active
 * campaign toggles, and the latest newsletter issues.
 *
 * Report data comes from the queryMyMarketing callable; the campaign list
 * reuses the automations DAL (same data the Campaigns page manages).
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  HStack,
  VStack,
  Text,
  Icon,
  Heading,
  Badge,
  Switch,
  Skeleton,
  Link,
  Flex,
  Tooltip,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  FiMail,
  FiEye,
  FiMousePointer,
  FiUserX,
  FiUsers,
  FiZap,
  FiExternalLink,
  FiSend,
} from 'react-icons/fi';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import moment from 'moment';
import useMyMarketing from '../../../../hooks/useMyMarketing';
import {
  fetchUserCampaigns,
  toggleUserCampaign,
  transformUserCampaignToCamelCase,
} from '../../../../api/supabase/automations';

const percent = (rate) => `${((rate || 0) * 100).toFixed(1)}%`;

function StatCard({ label, value, helpText, icon, colorScheme = 'blue', isLoaded = true, tooltip }) {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const iconBg = useColorModeValue(`${colorScheme}.50`, `${colorScheme}.900`);
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  const card = (
    <Box
      bg={bgColor}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      p={4}
      shadow="sm"
      transition="all 0.2s"
      _hover={{ shadow: 'md' }}
      h="100%"
    >
      <HStack justify="space-between" align="flex-start" spacing={2}>
        <Stat>
          <StatLabel fontSize="xs" color={labelColor} noOfLines={1}>
            {label}
          </StatLabel>
          <Skeleton isLoaded={isLoaded} mt={1} borderRadius="md" minH="36px">
            <StatNumber fontSize="2xl">{value}</StatNumber>
          </Skeleton>
          {helpText && (
            <StatHelpText mt={0} mb={0} fontSize="xs" noOfLines={1}>
              {helpText}
            </StatHelpText>
          )}
        </Stat>
        <Box bg={iconBg} p={2.5} borderRadius="lg">
          <Icon as={icon} boxSize={5} color={`${colorScheme}.500`} />
        </Box>
      </HStack>
    </Box>
  );

  return tooltip ? (
    <Tooltip label={tooltip} hasArrow placement="top" fontSize="xs">
      {card}
    </Tooltip>
  ) : card;
}

export default function OverviewTab({ uid, onGoToNewsletters }) {
  const toast = useToast();
  const { overview, overviewLoading, timeline, timelineLoading, loadOverview, loadTimeline } = useMyMarketing(uid);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const rowHoverBg = useColorModeValue('gray.50', 'gray.700');
  const axisColor = useColorModeValue('#718096', '#A0AEC0');

  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    loadOverview(30);
    loadTimeline(84);
  }, [loadOverview, loadTimeline]);

  const loadCampaigns = useCallback(async () => {
    if (!uid) return;
    try {
      const rows = await fetchUserCampaigns(uid);
      setCampaigns((rows || []).map(transformUserCampaignToCamelCase));
    } catch (err) {
      console.error('[OverviewTab] Campaign load failed:', err);
    } finally {
      setCampaignsLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const handleToggleCampaign = async (campaign) => {
    setTogglingId(campaign.id);
    try {
      await toggleUserCampaign(campaign.id, !campaign.isEnabled);
      setCampaigns((prev) =>
        prev.map((c) => (c.id === campaign.id ? { ...c, isEnabled: !campaign.isEnabled } : c))
      );
      toast({
        title: campaign.isEnabled ? 'Campaign paused' : 'Campaign activated',
        description: campaign.template?.name,
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (err) {
      toast({ title: 'Could not update campaign', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setTogglingId(null);
    }
  };

  const sends = overview?.sends || {};
  const engagement = overview?.engagement || {};
  const chartData = (timeline?.weeks || []).map((w) => ({
    week: moment(w.weekStart).format('MMM D'),
    Sent: w.sent,
    Opened: w.uniqueOpens,
    Clicks: w.clicks,
  }));

  return (
    <VStack align="stretch" spacing={5}>
      {/* KPI cards */}
      <SimpleGrid columns={{ base: 1, sm: 2, md: 3, xl: 6 }} spacing={3} data-tour="mm-kpis">
        <StatCard
          label="Emails Sent (30d)"
          value={sends.totalEmails ?? '—'}
          helpText={`${sends.newsletterEmails || 0} newsletter · ${sends.campaignEmails || 0} campaign`}
          icon={FiMail}
          colorScheme="blue"
          isLoaded={!overviewLoading}
          tooltip="All emails you sent from Origin CRM in the last 30 days — newsletters, campaign automations, and direct/bulk emails"
        />
        <StatCard
          label="Open Rate"
          value={overview ? percent(engagement.openRate) : '—'}
          helpText={`${engagement.uniqueOpens || 0} unique opens`}
          icon={FiEye}
          colorScheme="green"
          isLoaded={!overviewLoading}
        />
        <StatCard
          label="Click Rate"
          value={overview ? percent(engagement.clickRate) : '—'}
          helpText={`${engagement.uniqueClicks || 0} emails clicked`}
          icon={FiMousePointer}
          colorScheme="purple"
          isLoaded={!overviewLoading}
        />
        <StatCard
          label="Unsubscribes (30d)"
          value={overview?.unsubscribes ?? '—'}
          helpText="from your contacts"
          icon={FiUserX}
          colorScheme="red"
          isLoaded={!overviewLoading}
        />
        <StatCard
          label="Realtor Audience"
          value={overview?.audienceCount ?? '—'}
          helpText="emailable realtors"
          icon={FiUsers}
          colorScheme="teal"
          isLoaded={!overviewLoading}
          tooltip="Your real-estate-agent contacts with an email address who haven't unsubscribed or been marked Do Not Contact"
        />
        <StatCard
          label="Active Campaigns"
          value={overview?.activeCampaigns ?? '—'}
          helpText={`${overview?.newsletters?.active || 0} active newsletters`}
          icon={FiZap}
          colorScheme="orange"
          isLoaded={!overviewLoading}
        />
      </SimpleGrid>

      {/* Engagement chart */}
      <Box bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl" shadow="sm" p={5} data-tour="mm-chart">
        <Flex justify="space-between" align="center" mb={4}>
          <Box>
            <Heading size="sm">Email Engagement</Heading>
            <Text fontSize="xs" color={mutedColor}>Weekly sends, unique opens, and clicks — last 12 weeks</Text>
          </Box>
        </Flex>
        <Skeleton isLoaded={!timelineLoading} borderRadius="md">
          {chartData.length === 0 ? (
            <Flex h="220px" align="center" justify="center" direction="column" gap={2}>
              <Icon as={FiMail} boxSize={8} color="gray.300" />
              <Text fontSize="sm" color={mutedColor}>
                No email activity yet — engagement will appear here after your first sends
              </Text>
            </Flex>
          ) : (
            <Box h="260px">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <XAxis dataKey="week" tick={{ fontSize: 11, fill: axisColor }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: axisColor }} />
                  <RechartsTooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Sent" fill="#3182CE" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Opened" fill="#38A169" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Clicks" fill="#805AD5" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Skeleton>
      </Box>

      {/* Two-column: active campaigns + recent issues */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5}>
        <Box bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl" shadow="sm" p={5} data-tour="mm-campaigns-card">
          <Flex justify="space-between" align="center" mb={3}>
            <Heading size="sm">My Campaigns</Heading>
            <Link as={RouterLink} to="/crm/campaigns" fontSize="xs" color="blue.500">
              <HStack spacing={1}>
                <Text>Manage</Text>
                <Icon as={FiExternalLink} boxSize={3} />
              </HStack>
            </Link>
          </Flex>
          <Skeleton isLoaded={!campaignsLoading} borderRadius="md">
            {campaigns.length === 0 ? (
              <Text fontSize="sm" color={mutedColor} py={4} textAlign="center">
                No campaigns subscribed yet — browse the Campaigns page to enroll in one.
              </Text>
            ) : (
              <VStack align="stretch" spacing={0} maxH="280px" overflowY="auto">
                {campaigns.map((c) => (
                  <Flex
                    key={c.id}
                    justify="space-between"
                    align="center"
                    py={2.5}
                    px={2}
                    borderRadius="md"
                    _hover={{ bg: rowHoverBg }}
                  >
                    <Box minW={0} pr={3}>
                      <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                        {c.template?.name || 'Campaign'}
                      </Text>
                      <Text fontSize="xs" color={mutedColor} noOfLines={1}>
                        {c.template?.category || 'automation'}
                      </Text>
                    </Box>
                    <Switch
                      size="sm"
                      colorScheme="green"
                      isChecked={c.isEnabled}
                      isDisabled={togglingId === c.id}
                      onChange={() => handleToggleCampaign(c)}
                    />
                  </Flex>
                ))}
              </VStack>
            )}
          </Skeleton>
        </Box>

        <Box bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl" shadow="sm" p={5} data-tour="mm-recent-issues">
          <Flex justify="space-between" align="center" mb={3}>
            <Heading size="sm">Recent Newsletter Issues</Heading>
            <Link fontSize="xs" color="blue.500" onClick={onGoToNewsletters}>
              View all
            </Link>
          </Flex>
          <Skeleton isLoaded={!overviewLoading} borderRadius="md">
            {(overview?.recentIssues || []).length === 0 ? (
              <Flex direction="column" align="center" py={6} gap={2}>
                <Icon as={FiSend} boxSize={7} color="gray.300" />
                <Text fontSize="sm" color={mutedColor}>
                  No issues sent yet — create a newsletter to get started.
                </Text>
              </Flex>
            ) : (
              <VStack align="stretch" spacing={0}>
                {(overview?.recentIssues || []).map((issue) => {
                  const openRate = issue.sent_count > 0
                    ? Number(issue.unique_opens || 0) / issue.sent_count
                    : 0;
                  return (
                    <Flex
                      key={issue.id}
                      justify="space-between"
                      align="center"
                      py={2.5}
                      px={2}
                      borderRadius="md"
                      _hover={{ bg: rowHoverBg }}
                    >
                      <Box minW={0} pr={3}>
                        <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                          {issue.subject || '(no subject)'}
                        </Text>
                        <Text fontSize="xs" color={mutedColor}>
                          {moment(issue.created_at).format('MMM D, h:mm A')} · {issue.sent_count || 0} sent
                        </Text>
                      </Box>
                      <HStack spacing={2} flexShrink={0}>
                        {issue.status === 'sending' ? (
                          <Badge colorScheme="blue" variant="subtle">Sending…</Badge>
                        ) : (
                          <Badge colorScheme="green" variant="subtle">{percent(openRate)} opened</Badge>
                        )}
                      </HStack>
                    </Flex>
                  );
                })}
              </VStack>
            )}
          </Skeleton>
        </Box>
      </SimpleGrid>
    </VStack>
  );
}
